@if (driver(); as d) {
<div class="flex flex-col gap-4 p-4 h-full overflow-y-auto">
  <!-- Header: Avatar + Name & Preferred Name -->
  <div class="flex items-center gap-4 pb-4 border-b border-border/30">
    <div class="w-14 h-14 rounded-full bg-primary-low-contrast flex items-center justify-center text-primary font-bold text-xl shrink-0">
      {{ legalName()[0] | uppercase }}
    </div>
    <div class="min-w-0 flex-1">
      <app-heading level="h3" class="truncate">{{ legalName() }}</app-heading>
      @if (preferredName(); as pref) {
        <app-text size="xs" class="text-primary font-medium mt-0.5 block">
          Preferred Name: "{{ pref }}"
        </app-text>
      }
      <div class="flex items-center gap-2 mt-1.5 flex-wrap">
        @if (d.employmentStatus === 'terminated') {
          <app-badge variant="danger" prefixIcon="UserX" title="Terminated" size="xs" />
        } @else if (d.appStatus === 'manual') {
          <app-badge variant="secondary" title="Manual Dispatched" size="xs" />
        } @else {
          <app-badge variant="success" title="App Active" size="xs" />
        }
        <app-text size="xs" color="muted">
          {{ (d.isOwnerOperator || d.currentCompanyRoles.includes('owner_operator')) ? 'Owner Operator' : 'Company Driver' }}
        </app-text>
      </div>
    </div>
  </div>

  <!-- Terminated Status Callout -->
  @if (d.employmentStatus === 'terminated') {
    <div
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
      class="p-3.5 rounded-xl bg-surface border border-danger flex flex-col gap-2"
    >
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <lucide-icon name="UserX" class="w-4 h-4 text-danger shrink-0"></lucide-icon>
          <app-text size="sm" weight="semibold" color="danger">
            Terminated
            @if (d.terminatedDate) {
              on {{ d.terminatedDate | date:'mediumDate':'UTC' }}
            } @else if (d.terminatedAt) {
              on {{ d.terminatedAt | date:'mediumDate' }}
            }
          </app-text>
        </div>
      </div>
      @if (d.terminationReason) {
        <app-text size="xs" color="muted">Reason: {{ d.terminationReason }}</app-text>
      }
      @if (canManageEmployment()) {
        <app-button
          variant="primary"
          size="sm"
          icon="RotateCcw"
          class="w-full mt-1"
          (click)="reactivateDriver(d.id)"
        >
          Reactivate Driver
        </app-button>
      }
    </div>
  }

  <!-- Primary Action: Open 360 View -->
  <app-button
    [variant]="d.employmentStatus === 'terminated' ? 'secondary' : 'primary'"
    size="md"
    icon="Eye"
    class="w-full"
    (click)="view360(d.id)"
  >
    View Driver 360 Details
  </app-button>

  <!-- Employment Timelines Snapshot -->
  @if (timelines().length > 0) {
    <div class="space-y-3 bg-surface-high rounded-lg p-3">
      <div class="flex items-center justify-between">
        <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Employment History</app-text>
        <app-badge
          size="xs"
          variant="secondary"
          [title]="timelines().length + (timelines().length === 1 ? ' Tenure' : ' Tenures')"
        />
      </div>
      <div class="space-y-2">
        @for (span of timelines(); track span.tenureNumber) {
          <div class="p-2.5 rounded-lg bg-surface border border-border flex flex-col gap-1">
            <div class="flex items-center justify-between gap-2">
              <app-text size="xs" weight="semibold">
                {{ span.startDate ? (span.startDate | date:'MMM d, y':'UTC') : 'Initial Hire' }}
                →
                {{ span.endDate ? (span.endDate | date:'MMM d, y':'UTC') : 'Present' }}
              </app-text>
              <app-badge
                size="xs"
                [variant]="span.status === 'active' ? 'success' : 'secondary'"
                [title]="span.status === 'active' ? 'Active' : 'Ended'"
              />
            </div>
            @if (span.terminationReason) {
              <app-text size="xs" color="muted" class="truncate">{{ span.terminationReason }}</app-text>
            }
          </div>
        }
      </div>
    </div>
  }

  <!-- Contact Info -->
  <div class="space-y-3 bg-surface-high rounded-lg p-3">
    <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Contact</app-text>
    <div class="space-y-2">
      <div class="flex items-center gap-2.5">
        <lucide-icon name="Phone" class="w-4 h-4 text-muted shrink-0"></lucide-icon>
        <app-text size="sm">{{ d.phone ? (d.phone | formatPhone) : 'No phone registered' }}</app-text>
      </div>
      <div class="flex items-center gap-2.5">
        <lucide-icon name="Mail" class="w-4 h-4 text-muted shrink-0"></lucide-icon>
        <app-text size="sm" class="truncate">{{ d.contact?.email || 'No email registered' }}</app-text>
      </div>
    </div>
  </div>

  <!-- Compliance Snapshot -->
  <div class="space-y-3 bg-surface-high rounded-lg p-3">
    <div class="flex items-center justify-between">
      <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Compliance</app-text>
      <app-badge [variant]="cdlStatus().variant" [title]="cdlStatus().label" size="xs" />
    </div>
    <div class="space-y-2">
      <div class="flex justify-between items-center">
        <app-text size="sm" color="muted">CDL Number</app-text>
        <app-text size="sm" weight="semibold" class="font-mono">{{ d.cdlInfo?.number || '—' }}</app-text>
      </div>
      <div class="flex justify-between items-center">
        <app-text size="sm" color="muted">Class &amp; State</app-text>
        <app-text size="sm">Class {{ d.cdlInfo?.class || 'A' }} ({{ d.cdlInfo?.state || 'CA' }})</app-text>
      </div>
      <div class="flex justify-between items-center">
        <app-text size="sm" color="muted">CDL Expiration</app-text>
        <app-text size="sm">
          {{ d.cdlInfo?.expirationDate ? (d.cdlInfo?.expirationDate | date:'mediumDate':'UTC') : '—' }}
        </app-text>
      </div>
    </div>
  </div>

  <!-- Assigned Equipment -->
  @if (d.employmentStatus === 'active') {
    <div class="space-y-3 bg-surface-high rounded-lg p-3">
      <div class="flex items-center justify-between">
        <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Equipment</app-text>
        <app-button variant="ghost" size="sm" icon="Pencil" (click)="assignEquipment(d.id)">Edit</app-button>
      </div>
      <div class="space-y-2">
        <div class="flex justify-between items-center">
          <app-text size="sm" color="muted">Tractor / Truck</app-text>
          @if (d.assignedEquipment?.truckUnitNumber) {
            <app-text size="sm" weight="bold" class="text-primary">#{{ d.assignedEquipment?.truckUnitNumber }}</app-text>
          } @else {
            <app-text size="sm" color="muted">None</app-text>
          }
        </div>
        <div class="flex justify-between items-center">
          <app-text size="sm" color="muted">Coupled Trailer</app-text>
          @if (d.assignedEquipment?.trailerUnitNumber) {
            <app-text size="sm" weight="bold" class="text-primary">#{{ d.assignedEquipment?.trailerUnitNumber }}</app-text>
          } @else {
            <app-text size="sm" color="muted">None</app-text>
          }
        </div>
      </div>
    </div>
  }

  <!-- Pay Model Snapshot -->
  @if (payStructure(); as ps) {
    <div class="space-y-3 bg-surface-high rounded-lg p-3">
      <div class="flex items-center justify-between">
        <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Compensation</app-text>
        <app-badge variant="primary" [title]="getPayTypeLabel(ps.payType, ps.flatRatePeriod)" size="xs" />
      </div>
      <div class="space-y-2">
        @if (ps.payType === 'PER_MILE') {
          <div class="flex justify-between items-center">
            <app-text size="sm" color="muted">Loaded Rate</app-text>
            <app-text size="sm" weight="semibold">${{ ps.loadedMileRate | number:'1.2-2' }}/mi</app-text>
          </div>
          @if ((ps.emptyMileRate ?? 0) > 0) {
            <div class="flex justify-between items-center">
              <app-text size="sm" color="muted">Empty Rate</app-text>
              <app-text size="sm" weight="semibold">${{ ps.emptyMileRate | number:'1.2-2' }}/mi</app-text>
            </div>
          }
        } @else if (ps.payType === 'PERCENTAGE') {
          <div class="flex justify-between items-center">
            <app-text size="sm" color="muted">Revenue Share</app-text>
            <app-text size="sm" weight="semibold">{{ ps.grossRevenuePercent }}%</app-text>
          </div>
        } @else if (ps.payType === 'HOURLY') {
          <div class="flex justify-between items-center">
            <app-text size="sm" color="muted">Hourly Rate</app-text>
            <app-text size="sm" weight="semibold">${{ ps.hourlyRate | number:'1.2-2' }}/hr</app-text>
          </div>
        } @else if (ps.payType === 'FLAT') {
          <div class="flex justify-between items-center">
            <app-text size="sm" color="muted">Flat Rate</app-text>
            <app-text size="sm" weight="semibold">${{ ps.flatRateAmount || 0 | number:'1.2-2' }}</app-text>
          </div>
          <div class="flex justify-between items-center">
            <app-text size="sm" color="muted">Frequency</app-text>
            <app-text size="sm" weight="semibold">{{ getFlatRatePeriodLabel(ps.flatRatePeriod) }}</app-text>
          </div>
        }
      </div>
    </div>
  }

  <!-- Bottom Quick Actions -->
  <div class="flex flex-col gap-2 pt-2 mt-auto border-t border-border/30">
    @if (canManageDrivers() || canEditDriverPay()) {
      <div class="flex gap-2">
        @if (canManageDrivers()) {
          <app-button variant="secondary" size="sm" icon="Pencil" (click)="editProfile(d.id)" class="flex-1">
            Edit Profile
          </app-button>
        }
        @if (canEditDriverPay()) {
          <app-button variant="secondary" size="sm" icon="DollarSign" (click)="editPay(d.id)" class="flex-1">
            Edit Pay
          </app-button>
        }
      </div>
    }
    @if (d.employmentStatus === 'active' && canManageEmployment()) {
      <app-button variant="danger" size="sm" icon="UserX" (click)="terminateDriver(d.id)" class="w-full">
        Terminate Driver
      </app-button>
    }
  </div>
</div>
} @else {
<div class="flex flex-col items-center justify-center h-full text-center p-6">
  <lucide-icon name="Truck" class="w-12 h-12 text-muted opacity-30 mb-3"></lucide-icon>
  <app-text color="muted" size="sm">Select a commercial driver to view details</app-text>
</div>
}
